import { Component, Input, EventEmitter, Output, OnInit, TemplateRef } from '@angular/core';

/**
 * This component encapsulate the mat-radio-group control from angular material and uses the Tyler styling.
 * It creates a group of radio buttons that map to one variable.
 * NOTE: unlike the other components, this one does NOT use content projection. Anything placed inside the tags will be ignored.
 *
 * @input disabled: determines if the control should be disabled
 * @input labelPosition: 'before' or 'after' to indicate where labels should go. Defaults to 'after'.
 * @input name: the name of the group (for form purposes). Defaults to 'tyl-radio-group-#'.
 * @input required: whether the control is required or not. Defaults to false.
 * @input values: the values of each radio button. It also controls how many radio buttons show up.
 * @input labels: the labels of the radio buttons, in index order. If a value does not have a label, the value will be used instead.
 * @input ids: the ids of each radio button. Defaults to 'tyl-radio-button-#'.
 * @input disabledValues: a list of variables. If the value of any radio button is contained, it will be disabled.
 * @input disableIndexes: a list of numbers. If the index of a radio button is contained, it will be disabled.
 * @input arrange: this may be either 'horizontal' or 'vertical' to force a flex layout in that direction
 * @input spaceBetween: this is the extra distance between each radio button
 * @input label: this is an optional label that appears beside the radio group. Defaults to null.
 * @input labelWidth: the width of a displayed label. Defaults to null.
 * @input buttonAlignment: determines where the button should display when the text wraps.  Can be top, center, or bottom.
 *
 * @output change: an eventemitter that emits the most recently selected value.
 *
 * @input selected: this is the variable the control binds to.
 * @output selectedChange: this eventemitter emits the selected variable whenever it is changed.
 *
 * example:
 * <tyl-radio-group [(selected)]="food"
 *      [values]="['F', 'B', 'H']"
 *      [labels]="['Fries', 'Burgers', 'Hot Dogs']"
 *      [ids]="['friesOption', 'burgersOption', 'hotDogsOption']"
 *      (change)="validateFood()"></tyl-radio-group>
 */
@Component({
    selector: 'tyl-radio-group',
    templateUrl: './tyl-radio-group.component.html',
    styleUrls: [
        './tyl-radio-group.component.css'
    ]
})
export class TylRadioGroupComponent implements OnInit {

    // Static Properties
    static nameIterator = 0;

    // Interface Properties from MatRadioGroup
    @Input() disabled = false;
    @Input() labelPosition: 'before' | 'after' = 'after';
    @Input() name = 'tyl-radio-group-' + TylRadioGroupComponent.nameIterator++;
    @Input() required = false;
    @Output() change = new EventEmitter<any>();

    // Custom Interface Properties
    @Input() values: any[] = [];
    @Input() labels: string[] = [];
    @Input() ids: string[] = [];
    @Input() disabledValues: any[] = [];
    @Input() disabledIndexes: number[] = [];
    @Input() arrange: 'vertical' | 'horizontal';
    @Input() spaceBetween = '0px';
    @Input() wordWrap: boolean;
    @Input() label: string;
    @Input() labelWidth: number;
    @Input() contentTemplate: TemplateRef<any>;
    @Input() alignment: string;
    @Input() tabIndex = 0;
    @Input() tylTranslate = false;
    @Input() isError = false;
    @Input() errorMessage = '';
    @Input() buttonAlignment = 'center';

    private _selected: any;
    @Output() selectedChange = new EventEmitter<any>();

    // Local Properties

    // Class Functions
    get selected() {
        return this._selected;
    }

    @Input() set selected(value) {
        if (value !== this._selected) {
            this._selected = value;
            this.selectedChange.emit(this._selected);
        }
    }

    // Event Functions
    ngOnInit() {
        if (this.ids.length < this.values.length) {
            for (let i = this.values.length - this.ids.length; i < this.values.length; i++) {
                this.ids.push('tyl-radio-button-' + i);
            }
        }
    }

    // Methods

}
